import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipPortal, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useTagDetail } from '@/hooks/useTagQueries';
import { cn } from '@/lib/utils';
import { Tag as TagIcon } from 'lucide-react';

interface TagListProps {
  tagIds: number[];
  className?: string;
}

interface TagProps {
  tagId: number;
}

/**
 * Individual Tag component that fetches its own details
 * Only renders if tag exists
 * Note: System tags are already filtered out by the backend serializer
 */
function Tag({ tagId }: TagProps) {
  const { data: tag } = useTagDetail(tagId);

  // Don't render if tag doesn't exist
  if (!tag) {
    return null;
  }

  return (
    <Badge 
      variant="outline" 
      className="text-xs px-1.5 py-0.5 h-auto font-normal text-muted-foreground border-muted-foreground/30 bg-muted"
    >
      {tag.name}
    </Badge>
  );
}

export function TagList({ tagIds, className }: TagListProps) {
  return (
    <div className={`flex items-center gap-1 flex-wrap ${className || ''}`}>
      {tagIds.map((tagId) => (
        <Tag key={tagId} tagId={tagId} />
      ))}
    </div>
  );
}

/**
 * One-line stand-in for a content row's tags: a tag icon that reveals the tags on hover.
 * With no tags it renders a same-size blank so every row in a list keeps the same
 * horizontal rhythm whether or not the content is tagged.
 */
export function TagIndicator({ tagIds, className }: TagListProps) {
  const sizing = cn('h-4 w-4 shrink-0', className);

  if (tagIds.length === 0) {
    return <span className={sizing} aria-hidden="true" />;
  }

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(sizing, 'inline-flex items-center justify-center text-muted-foreground focus:outline-none')}
            aria-label={`${tagIds.length} ${tagIds.length === 1 ? 'tag' : 'tags'}`}
            data-testid="content-tag-indicator"
            onClick={(event) => event.stopPropagation()}
          >
            <TagIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent side="top" align="start" className="max-w-xs px-2 py-2">
            <TagList tagIds={tagIds} />
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}